import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFieldMode } from '../context/FieldModeContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  MobileFormHeader, StepIndicator, TouchButton, AutoSaveIndicator
} from './FieldModeComponents';
import {
  ArrowLeft, ArrowRight, Check, FileText, AlertTriangle,
  Shield, Plus, Trash2, CheckCircle, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MobileJHAForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isOnline, syncManager, offlineDB } = useFieldMode();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Job Info
    job_title: '',
    department: '',
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    
    // Step 2: Steps & Hazards
    job_steps: [
      { step: '', hazards: '', controls: '' }
    ],
    
    // Step 3: PPE & Training
    ppe_required: [],
    training_required: ''
  });

  const ppeOptions = [
    'Hard Hat', 'Safety Glasses', 'Face Shield', 'Hearing Protection',
    'Gloves', 'Steel Toe Boots', 'High Visibility Vest', 'Respirator',
    'Fall Protection', 'Chemical Suit'
  ];

  const saveDraft = useCallback(async () => {
    if (!formData.job_title) return;
    setIsSaving(true);
    try {
      await offlineDB.setUserData('jha_draft', { ...formData, updated_at: new Date().toISOString() });
      setLastSaved(new Date().toISOString());
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
    setIsSaving(false);
  }, [formData, offlineDB]);

  useEffect(() => {
    const interval = setInterval(() => {
      void saveDraft();
    }, 30000);
    return () => clearInterval(interval);
  }, [saveDraft]);

  useEffect(() => {
    const initDraft = async () => {
      try {
        const draft = await offlineDB.getUserData('jha_draft');
        if (draft) {
          setFormData(draft);
          setLastSaved(draft.updated_at);
          toast({ title: 'Draft Restored', description: 'Your previous draft has been loaded' });
        }
      } catch (error) {
        console.error('Failed to load draft:', error);
      }
    };
    initDraft();
  }, [offlineDB, toast]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStepChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      job_steps: prev.job_steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      job_steps: [...prev.job_steps, { step: '', hazards: '', controls: '' }]
    }));
  };

  const removeStep = (index) => {
    if (formData.job_steps.length > 1) {
      setFormData(prev => ({
        ...prev,
        job_steps: prev.job_steps.filter((_, i) => i !== index)
      }));
    }
  };

  const togglePPE = (ppe) => {
    setFormData(prev => ({
      ...prev,
      ppe_required: prev.ppe_required.includes(ppe)
        ? prev.ppe_required.filter(p => p !== ppe)
        : [...prev.ppe_required, ppe]
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    const jhaData = {
      job_title: formData.job_title,
      department: formData.department,
      date: formData.date,
      prepared_by: formData.prepared_by,
      job_steps: formData.job_steps,
      ppe_required: formData.ppe_required,
      training_required: formData.training_required,
      status: 'draft'
    };

    if (isOnline) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/jha-builder`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(jhaData)
        });

        if (response.ok) {
          await offlineDB.setUserData('jha_draft', null);
          setShowSuccess(true);
        } else {
          throw new Error('Failed to submit');
        }
      } catch (error) {
        await syncManager.queueForSync('jha', jhaData, '/api/jha-builder', 'create');
        await offlineDB.setUserData('jha_draft', null);
        setShowSuccess(true);
        toast({
          title: 'Saved Offline',
          description: 'JHA will sync when online',
          variant: 'warning'
        });
      }
    } else {
      await syncManager.queueForSync('jha', jhaData, '/api/jha-builder', 'create');
      await offlineDB.setUserData('jha_draft', null);
      setShowSuccess(true);
    }
    
    setIsSubmitting(false);
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const steps = [
    { number: 1, title: 'Job Info' },
    { number: 2, title: 'Steps' },
    { number: 3, title: 'PPE' }
  ];

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4" data-testid="jha-success">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">JHA Created!</h2>
            <p className="text-green-600 mb-6">
              {isOnline ? 'Your Job Hazard Analysis has been saved.' : 'Saved offline. Will sync when online.'}
            </p>
            <div className="space-y-3">
              <TouchButton onClick={() => navigate('/field')} variant="success">
                Back to Field Mode
              </TouchButton>
              <TouchButton onClick={() => {
                setShowSuccess(false);
                setCurrentStep(1);
                setFormData({
                  job_title: '', department: '',
                  date: new Date().toISOString().split('T')[0],
                  prepared_by: '',
                  job_steps: [{ step: '', hazards: '', controls: '' }],
                  ppe_required: [], training_required: ''
                });
              }} variant="outline">
                Create Another JHA
              </TouchButton>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mobile-jha-form">
      <MobileFormHeader
        title="Job Hazard Analysis"
        subtitle="JHA Builder"
        onBack={() => navigate('/field')}
        icon={FileText}
      />

      <div className="p-4 space-y-4">
        <StepIndicator steps={steps} currentStep={currentStep} />
        <AutoSaveIndicator lastSaved={lastSaved} isSaving={isSaving} />

        {/* Step 1: Job Info */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Job Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-base">Job Title *</Label>
                  <Input
                    value={formData.job_title}
                    onChange={(e) => handleChange('job_title', e.target.value)}
                    placeholder="e.g., Roof Installation"
                    className="h-12 text-base"
                  />
                </div>
                <div>
                  <Label className="text-base">Department</Label>
                  <Input
                    value={formData.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                    placeholder="e.g., Construction"
                    className="h-12 text-base"
                  />
                </div>
                <div>
                  <Label className="text-base">Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                    className="h-12 text-base"
                  />
                </div>
                <div>
                  <Label className="text-base">Prepared By</Label>
                  <Input
                    value={formData.prepared_by}
                    onChange={(e) => handleChange('prepared_by', e.target.value)}
                    placeholder="Your name"
                    className="h-12 text-base"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Job Steps & Hazards */}
        {currentStep === 2 && (
          <div className="space-y-4">
            {formData.job_steps.map((jobStep, index) => (
              <Card key={index}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Step {index + 1}</CardTitle>
                    {formData.job_steps.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStep(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-sm">Job Step *</Label>
                    <Input
                      value={jobStep.step}
                      onChange={(e) => handleStepChange(index, 'step', e.target.value)}
                      placeholder="Describe this step..."
                      className="h-11 text-base"
                    />
                  </div>
                  <div>
                    <Label className="text-sm flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      Hazards
                    </Label>
                    <Textarea
                      value={jobStep.hazards}
                      onChange={(e) => handleStepChange(index, 'hazards', e.target.value)}
                      placeholder="What could go wrong?"
                      className="min-h-[60px] text-base"
                    />
                  </div>
                  <div>
                    <Label className="text-sm flex items-center gap-1">
                      <Shield className="w-4 h-4 text-green-500" />
                      Controls
                    </Label>
                    <Textarea
                      value={jobStep.controls}
                      onChange={(e) => handleStepChange(index, 'controls', e.target.value)}
                      placeholder="How to prevent it?"
                      className="min-h-[60px] text-base"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
            
            <Button
              variant="outline"
              className="w-full h-12"
              onClick={addStep}
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Another Step
            </Button>
          </div>
        )}

        {/* Step 3: PPE & Training */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5 text-green-600" />
                  Required PPE
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {ppeOptions.map((ppe) => (
                    <button
                      key={ppe}
                      onClick={() => togglePPE(ppe)}
                      className={`p-3 rounded-lg text-left text-sm font-medium transition-all ${
                        formData.ppe_required.includes(ppe)
                          ? 'bg-green-100 border-2 border-green-500 text-green-800'
                          : 'bg-gray-100 border-2 border-transparent text-gray-700'
                      }`}
                    >
                      {ppe}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Training Required</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.training_required}
                  onChange={(e) => handleChange('training_required', e.target.value)}
                  placeholder="List any required training..."
                  className="min-h-[80px] text-base"
                />
              </CardContent>
            </Card>

            {!isOnline && (
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="py-4">
                  <p className="font-medium text-yellow-800">You&apos;re currently offline</p>
                  <p className="text-sm text-yellow-700">
                    Your JHA will be saved locally and synced when you&apos;re back online.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-3 pt-4 pb-8">
          {currentStep > 1 && (
            <TouchButton onClick={prevStep} variant="outline" className="flex-1">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </TouchButton>
          )}
          
          {currentStep < 3 ? (
            <TouchButton onClick={nextStep} className="flex-1">
              Continue
              <ArrowRight className="w-5 h-5 ml-2" />
            </TouchButton>
          ) : (
            <TouchButton
              onClick={handleSubmit}
              variant="success"
              className="flex-1"
              disabled={isSubmitting || !formData.job_title}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Save JHA
                </>
              )}
            </TouchButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileJHAForm;
